import { SingleReclamationViewApi } from "src/api/generated/erp/reclamations/api/singleReclamationViewApi.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { ReclamationView } from "src/api/generated/erp/db/types/tables/reclamationView.ts";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { ReclamationInfoView } from "src/components/views/erp/reclamation/singleReclamationView/ReclamationInfoView.tsx";
import i18n from "i18next";
import { SingleSourceTasksView } from "src/components/views/tasks/pages/SingleSourceTasksView.tsx";
import { CenteredTypography } from "src/components/common/CenteredTypography.tsx";
import { CostEventsOfObjectDataGrid } from "src/components/views/erp/common/costEvents/CostEventsOfObjectDataGrid.tsx";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import { ReclamationCustomerOrdersView } from "src/components/views/erp/reclamation/reclamationCustomerOrders/ReclamationCustomerOrdersView.tsx";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";

export interface SingleReclamationViewProps {
	reclamationId: number;
}

export const SingleReclamationView = ({ reclamationId }: SingleReclamationViewProps) => {
	return (
		<AsyncFetchRender
			fetch={async () => {
				const result = await SingleReclamationViewApi.getSingleReclamationViewData({
					reclamationId,
				});
				return result.reclamation;
			}}
			content={(reclamation, reload) => <AsyncContent reclamation={reclamation} refreshData={() => reload()} />}
		/>
	);
};

interface AsyncContentProps {
	reclamation: ReclamationView;
	refreshData: () => Promise<unknown>;
}

const AsyncContent = ({ reclamation, refreshData }: AsyncContentProps) => {
	const { tenantConfig } = useTenantCustomizations();

	return (
		<AavoMosaic
			viewId={"D263E1474C3B46D9"}
			layout={{
				type: "row",
				splitPercentage: 30,
				first: {
					type: "panel",
					content: <ReclamationInfoView reclamation={reclamation} refreshData={refreshData} />,
				},
				second: {
					type: "tabs",
					items: [
						{
							type: "panel",
							title: i18n.t("tasks"),
							content: (
								<SingleSourceTasksView
									key={`tasks-${reclamation.reclamationId}`}
									taskSourceRef={{
										sourceType: "RECLAMATION",
										sourceId: reclamation.reclamationId,
									}}
								/>
							),
						},
						{
							type: "panel",
							title: i18n.t("cost_events"),
							content:
								reclamation.costEventPriceListId == null ?
									<CenteredTypography>
										{i18n.t("cost_event_price_list_not_selected_for_reclamation")}
									</CenteredTypography>
								:	<CostEventsOfObjectDataGrid
										key={`cost-events-${reclamation.reclamationId}`}
										objectRef={{
											objectType: "RECLAMATION",
											objectId: reclamation.reclamationId,
										}}
										costEventPriceListId={reclamation.costEventPriceListId}
									/>,
						},
						tenantConfig.documentsEnabled && {
							type: "panel",
							title: i18n.t("documents"),
							content: (
								<DocumentsOfSingleObjectView
									key={`documents-${reclamation.reclamationId}`}
									objectRef={{
										objectType: "RECLAMATION",
										objectId: reclamation.reclamationId,
									}}
									editable
									hidePreview
								/>
							),
						},
						{
							type: "panel",
							title: i18n.t("connected_customer_orders"),
							content: (
								<ReclamationCustomerOrdersView
									key={`customer-orders-${reclamation.reclamationId}`}
									reclamationId={reclamation.reclamationId}
								/>
							),
						},
					],
				},
			}}
		/>
	);
};
